import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import {
  aclLink,
  batchAclLink,
  cameraGroupLink,
  cameraLink,
  createSuperAdminsLink,
  createUserLink,
  crossAgencyListLink,
  dashboardLink,
  editCameraGroupLink,
  editUserGroupLink,
  eventLogLink,
  liveViewLink,
  loginErrorLink,
  loginLink,
  managementLink,
  mapLink,
  playbackLink,
  profileLink,
  recordLink,
  superAdminLink,
  userGroupLink,
  userLink,
  userProfileLink,
  videoPlayerLink,
  videoStatusLink
} from './routerLink';
import {
  aclTabs,
  cameraTabs,
  recordingsTabs,
  superAdminTabs,
  userTabs
} from '../content/shared/tabItems';

import AppInit from 'src/components/AppInit';
import ExtendedSidebarLayout from '../layouts/ExtendedSidebarLayout';
import FullLayout from '../layouts/FullLayout';
import { LazyRetry } from './LazyRetry';
import Login from '../content/pages/Auth/Login/Cover';
import { ProtectedRoute } from './ProtectedRoute';
import SuspenseLoader from '../components/SuspenseLoader';

const Loader = (Component) => (props) =>
(
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

const lazyWithRetry = LazyRetry();

//landing page
const Selection = Loader(
  lazyWithRetry(() => import('../content/landingPage/Selection'))
);
// Applications

const LiveView = Loader(
  lazyWithRetry(() => import('../content/livestream/LiveView/Manager'))
);
const Users = Loader(
  lazyWithRetry(() => import('../content/management/Users/UserList'))
);
const CreateUsers = Loader(
  lazyWithRetry(() => import('../content/management/Users/CreateUser'))
);
const UserProfile = Loader(
  lazyWithRetry(()=>import("../content/management/Users/UserProfile"))
);
const SuperAdminList = Loader(
  lazyWithRetry(() =>
    import(
      '../content/management/SuperAdminUserManagement/SuperAdminManagement/SuperAdminList'
    )
  )
);
const CreateSuperAdmins = Loader(
  lazyWithRetry(() =>
    import(
      '../content/management/SuperAdminUserManagement/SuperAdminManagement/CreateSuperAdmin'
    )
  )
);
const CrossAgencyManagement = Loader(
  lazyWithRetry(()=> import('../content/management/SuperAdminUserManagement/CrossAgencyManagement/index'))
);
const UserGroups = Loader(
  lazyWithRetry(() => import('../content/management/UserGroups/index'))
);
const EditUserGroup = Loader(
  lazyWithRetry(() => import('../content/management/UserGroups/EditUserGroup'))
);
const ACLManagement = Loader(
  lazyWithRetry(() => import('../content/management/ACLmanagement'))
);
const BatchAccessControl = Loader(
  lazyWithRetry(() => import('../content/management/BatchAccessControl'))
);
const MapView = Loader(lazyWithRetry(() => import('../content/mapview/MapView')));
const Cameras = Loader(
  lazyWithRetry(() =>
    import(
      '../content/management/CamerasManagement/Cameras/CameraManagementList'
    )
  )
);
const CameraGroup = Loader(
  lazyWithRetry(() =>
    import('../content/management/CamerasManagement/CameraGroups/index')
  )
);
const EditCameraGroup = Loader(
  lazyWithRetry(() =>
    import(
      '../content/management/CamerasManagement/CameraGroups/EditCameraGroup'
    )
  )
);
const EventLog = Loader(lazyWithRetry(() => import('../content/management/EventLog')));
const Management = Loader(
  lazyWithRetry(() => import('../content/landingPage/ManagementSelection'))
);
const TabComponent = Loader(
  lazyWithRetry(() => import('../content/shared/TabComponent'))
);
const RecordedVideos = Loader(
  lazyWithRetry(() => import('../content/playback/RecordedVideos/index'))
);
const VideoStatus = Loader(
  lazyWithRetry(() => import('../content/playback/VideoStatus/index'))
);
const VideoPlayer = Loader( lazyWithRetry(()=> import('../content/playback/RecordedVideos/VideoPlayer')));

//base routes
const LoginError = Loader(
  lazyWithRetry(() => import('../content/pages/Status/LoginError'))
); 
const Status404 = Loader(
  lazyWithRetry(() => import('../content/pages/Status/Status404'))
);
const Status403 = Loader(
  lazyWithRetry(() => import('../content/pages/Status/Status403'))
);
 
const StatusComingSoon = Loader(
  lazyWithRetry(() => import('../content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazyWithRetry(() => import('../content/pages/Status/Maintenance'))
);

const ApplicationsRoutes = () => {
  return (
    <>
      <Routes>
        <Route exact path="/" element={<FullLayout />}>
          <Route index element={<AppInit />} />
          <Route path={loginLink} element={<Login />} />
        </Route>

        <Route path={dashboardLink} element={<FullLayout />}>
          <Route
            index
            element={
              <ProtectedRoute path={'/'} requestedPath='/'>
                {' '}
                <Selection />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path={profileLink} element={<ExtendedSidebarLayout />}>
          <Route
            index
            element={
              <ProtectedRoute path={'/'} requestedPath={profileLink}>
                <UserProfile />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path={liveViewLink} element={<ExtendedSidebarLayout />}>
          <Route
            index
            element={
              <ProtectedRoute path={'liveview'} requestedPath={liveViewLink}>
                <LiveView />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path={mapLink} element={<ExtendedSidebarLayout />}>
          <Route
            index
            element={
              <ProtectedRoute path={'map'} requestedPath={mapLink}>
                <MapView />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path={playbackLink} element={<ExtendedSidebarLayout />}>
          <Route
            element={
              <ProtectedRoute path={'recording'} requestedPath={recordLink}>
              <TabComponent
                tabs={recordingsTabs}
              />
              </ProtectedRoute>
            }
          >
            <Route
              index
              path={recordLink}
              element={<ProtectedRoute path={'recording'} requestedPath={recordLink}>
                <RecordedVideos />
                </ProtectedRoute>}
            />
           
            <Route
              index
              path={videoStatusLink}
              element={<ProtectedRoute path={'recording'} requestedPath={videoStatusLink}>
                <VideoStatus />
                </ProtectedRoute>}
            />
          </Route>
          <Route
              index
              path={videoPlayerLink}
              element={
                <ProtectedRoute path={'recording'} requestedPath={videoPlayerLink}>
                  <VideoPlayer />
                </ProtectedRoute>
              }
            />
        </Route>
        <Route path={managementLink} element={<ExtendedSidebarLayout />}>
          <Route
            index
            element={
              <ProtectedRoute path={'management'} requestedPath={managementLink}>
                <Management />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="/cameras" element={<ExtendedSidebarLayout />}>
          <Route
            element={
              <ProtectedRoute path={'management'} requestedPath={cameraLink}>
                <TabComponent
                  tabs={cameraTabs}
                />
              </ProtectedRoute>
            }
          >
            <Route
              index
              path={cameraLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={cameraLink}>
                  <Cameras />
                </ProtectedRoute>
              }
            />
            <Route
              index
              path={cameraGroupLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={cameraGroupLink}>
                  <CameraGroup />
                </ProtectedRoute>
              }
            />
            <Route
              index
              path={editCameraGroupLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={editCameraGroupLink}>
                  <EditCameraGroup />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        <Route path="/users" element={<ExtendedSidebarLayout />}>
          <Route
            element={
              <ProtectedRoute path={'management'}  requestedPath={userLink}>
                <TabComponent
                  tabs={userTabs}
                />
              </ProtectedRoute>
            }
          >
            <Route
              index
              path={userLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={userLink}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              index
              path={createUserLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={createUserLink}>
                  <CreateUsers />
                </ProtectedRoute>
              }
            />
            <Route
            index
            path = {userProfileLink}
            element={
              <ProtectedRoute path={'management'} requestedPath={userProfileLink}>
              <UserProfile />
            </ProtectedRoute>
            }
            />
            <Route
              index
              path={userGroupLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={userGroupLink}>
                  <UserGroups />
                </ProtectedRoute>
              }
            />
            <Route
              index
              path={editUserGroupLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={editUserGroupLink}>
                  <EditUserGroup />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        <Route path="/superAdmin" element={<ExtendedSidebarLayout />}>
          <Route
            element={
              <ProtectedRoute path={'superAdmin'} requestedPath={superAdminLink}>
                <TabComponent
                  tabs={superAdminTabs}
                />
              </ProtectedRoute>
            }
          >
            <Route
              index
              path={superAdminLink}
              element={
                <ProtectedRoute path={'superAdmin'} requestedPath={superAdminLink}>
                  <SuperAdminList />
                </ProtectedRoute>
              }
            />
            <Route
              index
              path={createSuperAdminsLink}
              element={
                <ProtectedRoute path={'superAdmin'} requestedPath={createSuperAdminsLink}>
                  <CreateSuperAdmins />
                </ProtectedRoute>
              }
            />
              <Route
              index
              path={crossAgencyListLink}
              element={
                <ProtectedRoute path={'superAdmin'} requestedPath={createSuperAdminsLink}>
                  <CrossAgencyManagement />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>
        <Route path="/access" element={<ExtendedSidebarLayout />}>
          <Route
            element={
              <ProtectedRoute path={'management'} requestedPath={aclLink}>
                <TabComponent tabs={aclTabs} />
              </ProtectedRoute>
            }
          >
            <Route
              index
              path={aclLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={aclLink}>
                  <ACLManagement />
                </ProtectedRoute>
              }
            />
            <Route
              index
              path={batchAclLink}
              element={
                <ProtectedRoute path={'management'} requestedPath={batchAclLink}>
                  <BatchAccessControl />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>
        <Route path={eventLogLink} element={<ExtendedSidebarLayout />}>
          <Route
            index
            element={
              <ProtectedRoute path={'management'} requestedPath={eventLogLink}>
                <EventLog />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path={loginErrorLink} element={<FullLayout />}>
          <Route index element={<LoginError />} />
        </Route>

        <Route path="status" element={<FullLayout />}> 
          <Route path="unauthorized" element={<Status403 />} />
          <Route path="maintenance" element={<StatusMaintenance />} />
          <Route path="coming-soon" element={<StatusComingSoon />} />
        </Route>

        <Route path="*" element={<Status404 />} />
      </Routes>
    </>
  );
};

export default ApplicationsRoutes;

